/* eslint-disable @typescript-eslint/no-unused-vars */
import { v4 as uuidv4 } from "uuid";
import { baseApi } from "./base.api";

export interface ChatMessage {
  message: string;
  messageType: "text" | "audio";
  sender: "user" | "agent";
  audioUrl?: string | File;
  tempId?: string;
  timestamp: string;
  isPending?: boolean;
}

export interface CreateChatContextRequest {
  agentId: string;
}

export interface CreateChatContextResponse {
  successMessage: string;
  chatId: string;
}

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createChatContext: builder.mutation<
      CreateChatContextResponse,
      CreateChatContextRequest & { headers?: Record<string, string> }
    >({
      query: (data) => {
        const { headers, ...rest } = data;
        return {
          url: "/agent/create_chat",
          method: "POST",
          body: rest,
          headers: {
            ...headers,
            authorization: headers?.authorization || undefined,
          },
        };
      },
      invalidatesTags: (result) =>
        result ? [{ type: "Chat", id: result.chatId }] : [],
    }),
    getChatMessages: builder.query<
      ChatMessage[],
      string | { headers?: Record<string, string>; contextId: string }
    >({
      query: (param) => {
        if (typeof param === "string") {
          return {
            url: `/agent/chat/${param}`,
            method: "GET",
          };
        }
        const { headers, contextId } = param;
        return {
          url: `/agent/chat/${contextId}`,
          method: "GET",
          headers,
        };
      },
      providesTags: (result, error, param) => [
        {
          type: "Chat" as const,
          id: typeof param === "string" ? param : param.contextId,
        },
      ],
      transformResponse: (res: { chat_context: ChatMessage[] }) =>
        res.chat_context,
    }),
    sendChatMessage: builder.mutation<
      ChatMessage,
      {
        contextId: string;
        message: Partial<ChatMessage> | FormData | string;
        headers?: Record<string, string>;
      }
    >({
      query: ({ contextId, message, headers }) => {
        console.debug("[sendChatMessage] Query params:", {
          contextId,
          message,
          headers,
        });
        if (message instanceof FormData) {
          return {
            url: `/agent/chat/${contextId}`,
            method: "POST",
            body: message,
            headers,
          };
        }
        return {
          url: `/agent/chat/${contextId}`,
          method: "POST",
          body: { message },
          headers,
        };
      },
      transformResponse: (res: {
        message: ChatMessage;
        userMessage: string;
      }) => {
        console.debug("[sendChatMessage] Transform response:", res);
        return res.message;
      },
      async onQueryStarted(
        { contextId, message },
        { dispatch, queryFulfilled }
      ) {
        console.debug("[sendChatMessage] onQueryStarted called with:", {
          contextId,
          message,
        });
        const timestamp = new Date().toISOString();
        const uniqueUserId = `${timestamp}-user-${Math.random()
          .toString(36)
          .slice(2, 8)}`;
        const uniqueAgentId = `${timestamp}-agent-${Math.random()
          .toString(36)
          .slice(2, 8)}`;

        console.debug("[sendChatMessage] Generated IDs:", {
          uniqueUserId,
          uniqueAgentId,
        });

        // Add user message optimistically
        console.debug("[sendChatMessage] Adding user message optimistically");
        dispatch(
          chatApi.util.updateQueryData(
            "getChatMessages",
            contextId,
            (draft) => {
              draft.push({
                message:
                  typeof message === "string"
                    ? message
                    : (message as Partial<ChatMessage>).message!,
                messageType: "text",
                sender: "user",
                timestamp,
                tempId: uniqueUserId,
              });
            }
          )
        );

        // Add pending agent message
        console.debug("[sendChatMessage] Adding pending agent message");
        dispatch(
          chatApi.util.updateQueryData(
            "getChatMessages",
            contextId,
            (draft) => {
              const pendingIndex = draft.findIndex((m) => m.isPending);
              if (pendingIndex !== -1) draft.splice(pendingIndex, 1);

              draft.push({
                message: "",
                messageType: "text",
                sender: "agent",
                timestamp,
                tempId: uniqueAgentId,
                isPending: true,
              });
            }
          )
        );

        try {
          console.debug("[sendChatMessage] Waiting for queryFulfilled");
          const { data: agentMessage } = await queryFulfilled;
          console.debug(
            "[sendChatMessage] queryFulfilled succeeded:",
            agentMessage
          );

          if (!agentMessage) {
            console.error("[sendChatMessage] No agent message in response");
            throw new Error("No agent message in response");
          }

          dispatch(
            chatApi.util.updateQueryData(
              "getChatMessages",
              contextId,
              (draft) => {
                const index = draft.findIndex(
                  (m) => m.tempId === uniqueAgentId
                );
                if (index !== -1) {
                  draft[index] = {
                    ...agentMessage,
                    tempId: undefined,
                    isPending: false,
                  };
                } else {
                  console.error(
                    "[sendChatMessage] Could not find pending message to update"
                  );
                }
              }
            )
          );
        } catch (error) {
          console.error("[sendChatMessage] Error in queryFulfilled:", error);
          dispatch(
            chatApi.util.updateQueryData(
              "getChatMessages",
              contextId,
              (draft) => {
                // Remove both optimistic updates on error
                return draft.filter(
                  (m) => m.tempId !== uniqueUserId && m.tempId !== uniqueAgentId
                );
              }
            )
          );
        }
      },
    }),
  }),
});

export const {
  useCreateChatContextMutation,
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
} = chatApi;
